import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  ArrowRight,
  BackupOutlined,
  CloseOutlined,
  Delete,
  StartOutlined,
  UploadFile,
  InsertDriveFile as InsertDriveFileIcon,
  Folder as FolderIcon,
  Search as SearchIcon,
  ExpandLess,
  ExpandMore
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Grid,
  LinearProgress,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  IconButton
} from "@mui/material";
import { useParams } from "react-router-dom";
import { BlobServiceClient } from "@azure/storage-blob";

const storageAccountUrl = "https://ogcorestr01.blob.core.windows.net";
const containerName = "fileuploader";
const sasToken =
  "sp=rl&st=2025-09-30T10:19:02Z&se=2025-10-29T18:34:02Z&spr=https&sv=2024-11-04&sr=c&sig=owYYXfJXToWXMyPMkYqGpbXz1Aq2%2Fm%2Fl8xBbMJq7iic%3D";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

type DocumentType = "jib" | "invoice" | "permit" | "pid" | "aef" | "contract";
type Result = {
  docText: string;
  extracted: Record<string, any>;
  fileName: string;
};

function DocumentUploader() {
  // Main states
  const [file, setFile] = useState<File[]>([]);
  const [result, setResult] = useState<Result[]>([]);
  const [openStates, setOpenStates] = useState<boolean[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Document/param logic
  const [docTypeInput, setDocTypeInput] = useState("");
  const [attrInput, setAttrInput] = useState("");
  const [descInput, setDescInput] = useState("");
  const [paramPairs, setParamPairs] = useState<
    { attribute: string; description: string }[]
  >([]);
  const params = useParams<{ docType: DocumentType }>();

  // Azure Blob Picker states
  const [containerFiles, setContainerFiles] = useState<string[]>([]);
  const [showBlobPicker, setShowBlobPicker] = useState(false);
  const [selectedBlobFiles, setSelectedBlobFiles] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<{
    [path: string]: boolean;
  }>({});

  // --- File Handlers ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const existingFileNames = new Set(file.map((f) => f.name));
      const uniqueNewFiles = newFiles.filter(
        (f) => !existingFileNames.has(f.name)
      );
      setFile((prevFiles) => [...prevFiles, ...uniqueNewFiles]);
    }
  };
  const handleRemove = (filenameToRemove: string) => {
    setFile((prevFiles) =>
      prevFiles.filter((f) => f.name !== filenameToRemove)
    );
  };
  const handleClearAll = () => setFile([]);

  // --- Azure Blob Picker Handlers ---
  const getBlobFiles = async () => {
    try {
      const blobServiceClient = new BlobServiceClient(
        `${storageAccountUrl}?${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);
      const blobNames: string[] = [];
      for await (const blob of containerClient.listBlobsFlat()) {
        blobNames.push(blob.name);
      }
      setContainerFiles(blobNames);
    } catch (err) {
      setError("Failed to list files from container.");
      setContainerFiles([]);
    }
  };
  function buildFolderTree(files: string[]) {
    const tree: any = {};
    files.forEach((path) => {
      const parts = path.split("/");
      let cur = tree;
      parts.forEach((part, idx) => {
        if (part.trim() === "") return;
        if (idx === parts.length - 1 && !path.endsWith("/")) {
          cur[part] = null;
        } else {
          cur[part] = cur[part] || {};
          cur = cur[part];
        }
      });
    });
    return tree;
  }
  function getAllFilesFromTree(node: any, prefix = ""): string[] {
    let files: string[] = [];
    Object.entries(node).forEach(([key, val]) => {
      if (val === null) {
        files.push(prefix + key);
      } else {
        files = files.concat(getAllFilesFromTree(val, prefix + key + "/"));
      }
    });
    return files;
  }
  function renderTree(node: any, prefix = "", level = 0) {
    return Object.entries(node).map(([key, val]) => {
      const isFile = val === null;
      const isFolder = !isFile && Object.keys(val as object).length > 0;
      const folderPath = prefix + key + "/";
      const isSelected = isFile && selectedBlobFiles.includes(prefix + key);
      const isOpen = expandedFolders[folderPath] || false;
      return (
        <Box key={prefix + key} ml={level * 2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 0.5,
              bgcolor: isSelected ? "#23232c" : "transparent",
              color: isFile ? (isSelected ? "#FFE600" : "#fff") : "#33ffd0",
              fontWeight: isFolder ? 700 : 400,
              cursor: isFile || isFolder ? "pointer" : "default",
              "&:hover": { opacity: 0.95, bgcolor: "#222" }
            }}
          >
            {isFolder ? (
              <IconButton
                size="small"
                sx={{ mr: 1, color: "#FFE600" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedFolders((exp) => ({
                    ...exp,
                    [folderPath]: !exp[folderPath]
                  }));
                }}
              >
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            ) : (
              <InsertDriveFileIcon sx={{ mr: 1 }} />
            )}
            {isFolder ? (
              <Box
                sx={{ display: "flex", alignItems: "center", flex: 1 }}
                onClick={() => {
                  // Select all files under this folder
                  const filesInFolder = getAllFilesFromTree(val, folderPath);
                  setSelectedBlobFiles(filesInFolder);
                  setShowBlobPicker(false);
                }}
              >
                <FolderIcon sx={{ mr: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                  title={key}
                >
                  {key}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
                title={key}
                onClick={() => {
                  setSelectedBlobFiles([prefix + key]);
                  setShowBlobPicker(false);
                }}
              >
                {key}
              </Typography>
            )}
          </Box>
          {isFolder && isOpen && renderTree(val, folderPath, level + 1)}
        </Box>
      );
    });
  }

  // --- Extraction Key-Value Pair Handlers ---
  const handleAddPair = () => {
    if (!attrInput.trim() || !descInput.trim()) return;
    setParamPairs([
      ...paramPairs,
      { attribute: attrInput.trim(), description: descInput.trim() }
    ]);
    setAttrInput("");
    setDescInput("");
  };
  const handleRemovePair = (idx: number) => {
    setParamPairs(paramPairs.filter((_, i) => i !== idx));
  };

  // --- Local Upload Handler ---
  const handleUpload = async () => {
    const docType = docTypeInput.trim() || params.docType?.toLowerCase();
    if (!docType) {
      setError("Please provide a document type!");
      return;
    }
    if (file.length === 0) {
      setError("Please select at least one file!");
      return;
    }
    setError("");
    const paramObj: Record<string, string> = {};
    paramPairs.forEach((pair) => {
      paramObj[pair.attribute] = pair.description;
    });
    const formData = new FormData();
    formData.append("doc_type", docType);
    file.forEach((f) => {
      formData.append("files", f);
    });
    formData.append("paramters", JSON.stringify(paramObj));
    try {
      setLoading(true);
      const res = await axios.post(
        "https://og-core-wap-01-bkhxdmegehcue2cu.eastus-01.azurewebsites.net/upload_custom_documents",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setLoading(false);
      setResult(res.data || []);
      setOpenStates(new Array((res.data || []).length).fill(true));
      setError("");
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload or parse documents.");
      setLoading(false);
    }
  };

  // --- Azure Blob "Upload" Handler ---
  const handleSendBlobUrls = async () => {
    if (selectedBlobFiles.length === 0) {
      setError("No blob files selected.");
      return;
    }
    // Construct blob URLs as per API
    const blobUrls = selectedBlobFiles.map(
      (name) => `${storageAccountUrl}/${containerName}/${name}`
    );
    const docType =
      docTypeInput.trim() || params.docType?.toLowerCase() || "plot_plan";
    const paramObj: Record<string, string> = {};
    paramPairs.forEach((pair) => {
      paramObj[pair.attribute] = pair.description;
    });
    try {
      setLoading(true);
      const res = await axios.post(
        "https://og-core-wap-01-bkhxdmegehcue2cu.eastus-01.azurewebsites.net/blob_custom_upload",
        {
          blob_files: blobUrls,
          doc_type: docType,
          paramters: paramObj
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setLoading(false);
      setResult(res.data || []);
      setOpenStates(new Array((res.data || []).length).fill(true));
      setError("");
    } catch (err) {
      setError("Failed to send Blob URLs to backend.");
      setLoading(false);
    }
  };

  // --- Download Excel ---
  const handleDownloadXL = (data: Result[]) => {
    if (!Array.isArray(data) || data.length === 0) {
      alert("No data to export.");
      return;
    }
    const fileNames = data.map((d) => d.fileName);
    const allKeys = Array.from(
      data.reduce((set, res) => {
        Object.keys(res.extracted).forEach((key) => set.add(key));
        return set;
      }, new Set<string>())
    );
    const xlRows = allKeys.map((key) => {
      const row: Record<string, any> = { Field: key };
      data.forEach((item, idx) => {
        const v = item.extracted[key];
        row[fileNames[idx]] = Array.isArray(v)
          ? v.join(", ")
          : v !== undefined && v !== null && v !== ""
          ? v
          : "-";
      });
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(xlRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Extracted Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    saveAs(blob, "extracted_data.xlsx");
  };

  // --- Transposed Table of Results ---
  const renderTransposedTable = (results: Result[]) => {
    if (!results.length) return null;

    // Normalize extracted field: always use plain object
    const normalizedResults = results.map((res) => ({
      ...res,
      extracted:
        Array.isArray(res.extracted) && res.extracted.length === 1
          ? res.extracted[0]
          : !Array.isArray(res.extracted) && typeof res.extracted === "object"
          ? res.extracted
          : {}
    }));

    const fileNames = normalizedResults.map((r) => r.fileName);

    const allKeys = Array.from(
      normalizedResults.reduce((set, res) => {
        Object.keys(res.extracted)
          .filter((key) => isNaN(Number(key))) // Skip numeric keys such as "0"
          .forEach((key) => set.add(key));
        return set;
      }, new Set<string>())
    );

    return (
      <TableContainer
        sx={{
          mt: 2,
          overflowX: "auto",
          border: "1px solid #ccc",
          borderRadius: 2,
          bgcolor: "#18181e",
          width: "100%",
          minWidth: 700,
          boxShadow: 1
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ "& th": { background: "#21212e" } }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "grey", fontWeight: 800, minWidth: 150 }}>
                Attribute Name
              </TableCell>
              {fileNames.map((fn, idx) => (
                <TableCell
                  key={idx}
                  sx={{ color: "grey", fontWeight: 700, minWidth: 250 }}
                >
                  Attribute Value
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#fff", fontWeight: 800, minWidth: 150 }}>
                File name
              </TableCell>
              {fileNames.map((fn, idx) => (
                <TableCell
                  key={idx}
                  sx={{ color: "#fff", fontWeight: 700, minWidth: 250 }}
                >
                  {fn}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allKeys.map((key, rIdx) => (
              <TableRow key={rIdx}>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                  {key}
                </TableCell>
                {normalizedResults.map((res, cIdx) => {
                  const value = res.extracted[key];
                  return (
                    <TableCell
                      key={cIdx}
                      sx={{ color: "#fff", fontWeight: 400 }}
                    >
                      {Array.isArray(value)
                        ? value.length > 0
                          ? value.join(", ")
                          : "-"
                        : value !== undefined && value !== null && value !== ""
                        ? String(value)
                        : "-"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // --- UI ---
  const filteredFiles = containerFiles.filter((name) =>
    name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <Box sx={{ margin: "0 auto", p: 2 }}>
      {/* Document Type */}
      <Card
        sx={{
          mb: 3,
          p: 3,
          backgroundColor: "rgba(0,0,0,0.2)",
          color: "#fff",
          border: "1px solid #fff"
        }}
      >
        <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
          Document Type
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          value={docTypeInput}
          onChange={(e) => setDocTypeInput(e.target.value)}
          placeholder="e.g., contract, invoice, receipt"
          sx={{
            background: "#fff",
            mb: 2
          }}
          helperText={
            params.docType
              ? `URL param docType: ${params.docType} (overridden if you type)`
              : ""
          }
        />
      </Card>
      {/* --- Upload area --- */}
      <Card
        sx={{
          backgroundColor: "rgba(0,0,0,0.2)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 3,
          padding: 2,
          border: "1px solid #fff"
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          flexDirection="row"
          gap={2}
          flex={1}
        >
          <BackupOutlined fontSize="large" />
          <Typography>Drag and Drop files here</Typography>
        </Box>
        <Button
          component="label"
          variant="outlined"
          sx={{
            fontWeight: 700,
            minWidth: 150,
            borderRadius: 2,
            ml: "auto",
            color: "#FFE600",
            borderColor: "#FFE600"
          }}
          size="large"
        >
          Browse files
          <VisuallyHiddenInput type="file" multiple onChange={handleChange} />
        </Button>
        <Button
          variant="outlined"
          sx={{
            fontWeight: 700,
            minWidth: 150,
            borderRadius: 2,
            ml: 2,
            color: "#66ffe6",
            borderColor: "#66ffe6"
          }}
          size="large"
          onClick={async () => {
            setShowBlobPicker(true);
            await getBlobFiles();
          }}
        >
          Browse Storage
        </Button>
      </Card>
      {/* Azure Picker Modal */}
      {showBlobPicker && (
        <Box
          sx={{
            position: "fixed",
            top: "5vh",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2000,
            bgcolor: "rgba(24,24,30, 0.98)",
            background: "linear-gradient(120deg, #20202a 65%, #191924 100%)",
            p: 0,
            borderRadius: 4,
            border: "2.5px solid #393956",
            boxShadow: "0 12px 44px 0 #111b",
            width: { xs: "98vw", sm: 760, md: 960, lg: 1200 },
            maxWidth: "98vw",
            maxHeight: { xs: "91vh", sm: 700, md: 800, lg: 900 },
            minHeight: 420,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {/* Title Bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#20203a",
              p: 3,
              borderBottom: "2px solid #272748",
              position: "sticky",
              top: 0,
              zIndex: 2,
              height: 64
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                fontWeight: 500,
                fontSize: 28,
                flex: 1,
                letterSpacing: 1
              }}
            >
              Azure Container Browser
            </Typography>
            <IconButton size="large" onClick={() => setShowBlobPicker(false)}>
              <CloseOutlined sx={{ color: "#FFE600", fontSize: 32 }} />
            </IconButton>
          </Box>
          {/* Search */}
          <Box display="flex" alignItems="center" gap={2} px={5} pt={4} pb={2}>
            <SearchIcon sx={{ color: "#999", fontSize: 16 }} />
            <TextField
              size="medium"
              placeholder="Search files or folders..."
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                bgcolor: "#23232c",
                input: { color: "#fff", fontSize: 20 },
                "& fieldset": { borderColor: "#393956" },
                "& .MuiInputBase-input": { py: "15px" }
              }}
              InputProps={{ style: { fontSize: 18, letterSpacing: 0.2 } }}
            />
          </Box>
          <Box sx={{ px: 0, pt: 0.5, pb: 0 }}>
            <Box sx={{ mx: 5, my: 2 }}>
              <hr
                style={{
                  border: 0,
                  borderTop: "1.5px solid #313548",
                  margin: 0
                }}
              />
            </Box>
          </Box>
          {/* File View */}
          <Box
            sx={{
              flex: 1,
              minHeight: 300,
              overflowY: "auto",
              bgcolor: "transparent",
              px: 5,
              pb: 4,
              borderRadius: 2,
              "&::-webkit-scrollbar": {
                width: 13,
                background: "#24243a",
                borderRadius: 2
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#393956",
                borderRadius: 100
              }
            }}
          >
            {filteredFiles.length === 0 ? (
              <Typography
                sx={{
                  color: "#bbb",
                  textAlign: "center",
                  mt: 14,
                  fontSize: 26,
                  fontWeight: 400
                }}
              >
                No files found.
              </Typography>
            ) : (
              renderTree(buildFolderTree(filteredFiles))
            )}
          </Box>
        </Box>
      )}

      {/* Azure Selection & Upload */}
      {selectedBlobFiles.length > 0 && (
        <Box marginTop={2} marginBottom={2}>
          <Typography sx={{ color: "#66ffe6", mb: 1 }}>
            Selected files
          </Typography>
          {selectedBlobFiles.map((f, idx) => (
            <Box key={f} mb={1} display="flex" justifyContent="space-between">
              <Box display={"flex"} gap={2} alignItems="center">
                <ArrowRight />
                <Typography sx={{ color: "#ffffff" }}>{f}</Typography>
              </Box>
              <CloseOutlined
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  setSelectedBlobFiles(selectedBlobFiles.filter((x) => x !== f))
                }
              />
            </Box>
          ))}
          <Grid display="flex" justifyContent="space-between" gap={2} mb={2}>
            <Button
              variant="outlined"
              sx={{ color: "#FFE600", borderColor: "#FFE600" }}
              onClick={handleSendBlobUrls}
              disabled={loading || selectedBlobFiles.length === 0}
            >
              Upload
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setSelectedBlobFiles([])}
              disabled={selectedBlobFiles.length === 0}
            >
              Clear All
            </Button>
          </Grid>
        </Box>
      )}

      {/* Local File List & Upload */}
      {file.length > 0 && (
        <Box marginTop={2} marginBottom={2}>
          <Typography sx={{ color: "#ffffff", mb: 1 }}>
            Selected files:
          </Typography>
          {file.map((f, idx) => (
            <Box key={idx} mb={1} display="flex" justifyContent="space-between">
              <Box display={"flex"} gap={2} alignItems="center">
                <ArrowRight />
                <Typography sx={{ color: "#ffffff" }}>{f.name}</Typography>
              </Box>
              <CloseOutlined
                sx={{ cursor: "pointer" }}
                onClick={() => handleRemove(f.name)}
              />
            </Box>
          ))}
        </Box>
      )}
      {file.length > 0 && (
        <Grid display="flex" justifyContent="space-between" gap={2} mb={2}>
          <Button
            startIcon={<UploadFile />}
            variant="outlined"
            sx={{ color: "#FFE600", borderColor: "#FFE600" }}
            onClick={handleUpload}
          >
            Upload
          </Button>
          <Button
            startIcon={<Delete />}
            variant="outlined"
            color="error"
            onClick={handleClearAll}
          >
            Clear All
          </Button>
        </Grid>
      )}

      {/* Extraction Keys */}
      <Card
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: "rgba(0,0,0,0.2)",
          color: "#fff",
          border: "1px solid #fff",
          borderRadius: 1
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Attributes
        </Typography>
        {paramPairs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {paramPairs.map((pair, idx) => (
              <Stack direction="row" alignItems="center" spacing={2} key={idx}>
                <Typography sx={{ color: "#FFE600" }}>
                  <strong>{pair.attribute}</strong>
                </Typography>
                <Typography sx={{ color: "#fff" }}>
                  {pair.description}
                </Typography>
                <IconButton size="small" onClick={() => handleRemovePair(idx)}>
                  <CloseOutlined fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Box>
        )}
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <div style={{ minWidth: 180 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#fff" }}>
              Attribute
            </Typography>
            <TextField
              placeholder="eg: Invoice Number"
              variant="outlined"
              value={attrInput}
              size="medium"
              onChange={(e) => setAttrInput(e.target.value)}
              sx={{ background: "#fff", minWidth: 180 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#fff" }}>
              Description
            </Typography>
            <TextField
              placeholder="Invoice Number: Unique identifier of the invoice."
              multiline
              variant="outlined"
              value={descInput}
              size="medium"
              onChange={(e) => setDescInput(e.target.value)}
              sx={{ background: "#fff", minWidth: 100, flex: 1 }}
              fullWidth
            />
          </div>
          <Button
            variant="contained"
            onClick={handleAddPair}
            sx={{
              height: 48,
              bgcolor: "#FFE600",
              color: "#222",
              fontWeight: "bold",
              ":hover": { bgcolor: "#f9ce00" }
            }}
          >
            Add
          </Button>
        </Stack>
      </Card>
      {loading && (
        <Box marginTop={4}>
          <p>Extraction in progress.....</p>
          <LinearProgress
            sx={{
              backgroundColor: "#FFE600",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#ffffff"
              }
            }}
          />
        </Box>
      )}
      {!loading && result.length > 0 && (
        <Box mt={1}>
          <Typography variant="h6" sx={{ px: 2, mb: 1 }}>
            Summary
          </Typography>
          {renderTransposedTable(result)}
          <Button
            startIcon={<StartOutlined />}
            onClick={() => handleDownloadXL(result)}
            variant="outlined"
            sx={{ mt: 2, width: 320, color: "#FFE600", borderColor: "#FFE600" }}
          >
            Download
          </Button>
        </Box>
      )}
      {error && (
        <Box
          sx={{ mt: 2, p: 2, bgcolor: "rgba(255,0,0,0.09)", borderRadius: 1 }}
        >
          <Typography style={{ color: "red", whiteSpace: "pre-wrap" }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default DocumentUploader;
